/**
 * Start here — the single front door. Two questions route a visitor to the right
 * free diagnostic instead of making them choose between 12 lenses and ValueStream360.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Layers, GitBranch, Compass, ArrowRight } from "lucide-react";

const OPTIONS = [
  {
    id: "function",
    to: "/resources",
    icon: Layers,
    eyebrow: "A specific capability",
    title: "Assess one function",
    body: "Score a single capability — IT service, PMO, supplier management, data, change and more. ~3 minutes, free, no sign-in.",
    cta: "Choose a lens",
  },
  {
    id: "value-stream",
    to: "/value-stream-assessment",
    icon: GitBranch,
    eyebrow: "An end-to-end process",
    title: "Assess a value stream",
    body: "Score an end-to-end flow — source-to-pay, order-to-cash, idea-to-market — across the functions that touch it.",
    cta: "Choose a value stream",
  },
  {
    id: "whole",
    to: "/the-360-family",
    icon: Compass,
    eyebrow: "The whole picture",
    title: "See the full method",
    body: "Not sure where to start? See how the twelve lenses and the ENTERPRISE360 engine work as one joined-up method.",
    cta: "Explore the 360 family",
  },
];

export default function StartHere() {
  return (
    <div data-testid="start-here-page">
      <section className="relative ek-gradient-dark text-white overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA] ek-fade-up">Start here</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white mt-5 max-w-3xl ek-fade-up" style={{ animationDelay: "60ms" }} data-testid="start-here-headline">
            What would you like to understand?
          </h1>
          <p className="text-zinc-300 font-light text-lg mt-6 max-w-2xl ek-fade-up" style={{ animationDelay: "120ms" }}>
            Pick the lens that fits. Every route is a free, sector-calibrated diagnostic — and they all feed the same
            joined-up maturity picture.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {OPTIONS.map((o, i) => {
              const Icon = o.icon;
              return (
                <Link key={o.id} to={o.to} data-testid={`start-option-${o.id}`}
                  className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#A179FA]/50 rounded-xl p-6 transition-colors ek-fade-up flex flex-col"
                  style={{ animationDelay: `${180 + i * 70}ms` }}>
                  <Icon size={22} className="text-[#A179FA]" />
                  <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-400 mt-4">{o.eyebrow}</div>
                  <h2 className="text-xl font-semibold mt-1">{o.title}</h2>
                  <p className="text-zinc-300 text-[14px] font-light mt-2 flex-1">{o.body}</p>
                  <span className="inline-flex items-center gap-1.5 text-[#A179FA] text-[13px] mt-4 group-hover:gap-2.5 transition-all">
                    {o.cta} <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
